import type { ReactNode } from 'react';
import { AlertTriangle, ArrowRight, CalendarClock, Flag } from 'lucide-react';
import { formatDateOnly, formatFriendlyDateOnly } from '../../lib/dates';
import {
  ragDotClass,
  ragLabel,
  serviceRequestCardProgress,
  serviceRequestNextAction,
  serviceRequestPriorityLabel,
  type ServiceRequestListItem,
} from '../../lib/service-request-views';
import { cn } from '../../lib/utils';

/** Deadlines only count once the request has been accepted. */
const PRE_APPROVAL_STAGES = new Set(['draft', 'submitted', 'under_review', 'changes_required']);

function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase() ?? '').join('');
}

function DueChip({ request }: { request: ServiceRequestListItem }) {
  const target = request.target_go_live_date;
  if (!target) return null;
  const days = request.sla?.target_days_remaining ?? null;
  const overdue = request.sla?.target_overdue === true;
  const soon = !overdue && days !== null && days <= 7;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 text-[12px] tabular-nums',
        overdue ? 'font-medium text-rose-600 dark:text-rose-300' : soon ? 'font-medium text-amber-700 dark:text-amber-300' : 'text-muted-foreground',
      )}
      title={`Target go-live ${formatDateOnly(target)}`}
    >
      <CalendarClock className="h-3.5 w-3.5" aria-hidden />
      {goLiveText(target, days, overdue)}
    </span>
  );
}

/** "Go-live Fri 23 Oct", "Go-live in 3 days", "Go-live 2 days late". */
export function goLiveText(target: string, days: number | null, overdue: boolean): string {
  if (overdue && days !== null) return `Go-live ${Math.abs(days)} ${Math.abs(days) === 1 ? 'day' : 'days'} late`;
  if (overdue) return 'Go-live date passed';
  if (days === 0) return 'Go-live today';
  if (days !== null && days > 0 && days <= 14) return `Go-live in ${days} ${days === 1 ? 'day' : 'days'}`;
  return `Go-live ${formatFriendlyDateOnly(target) ?? target}`;
}

function StatusPill({ label, tone }: { label: string; tone: 'waiting' | 'active' | 'done' | 'attention' }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-semibold leading-none',
        tone === 'waiting' && 'bg-blue-100 text-blue-800 dark:bg-blue-500/20 dark:text-blue-200',
        tone === 'active' && 'bg-sky-100 text-sky-800 dark:bg-sky-500/20 dark:text-sky-200',
        tone === 'done' && 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-200',
        tone === 'attention' && 'bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-200',
      )}
    >
      <span className={cn('h-1.5 w-1.5 rounded-full', tone === 'waiting' ? 'bg-blue-500' : tone === 'active' ? 'bg-sky-500' : tone === 'attention' ? 'bg-amber-500' : 'bg-emerald-500')} aria-hidden />
      {label}
    </span>
  );
}

function Progress({ request }: { request: ServiceRequestListItem }) {
  const progress = serviceRequestCardProgress(request);
  if (progress.kind === 'status') {
    return <StatusPill label={progress.label} tone={request.stage === 'completed' ? 'done' : request.stage === 'changes_required' ? 'attention' : 'active'} />;
  }
  if (progress.kind === 'steps') {
    return (
      <div className="space-y-1.5">
        <StatusPill label={`${progress.label} · ${progress.done}/${progress.total}`} tone="waiting" />
        <div className="flex gap-1" aria-label={`${progress.label} ${progress.done} of ${progress.total}`}>
          {Array.from({ length: Math.max(progress.total, 1) }, (_, index) => (
            <span key={index} className={cn('h-1.5 flex-1 rounded-full', index < progress.done ? 'bg-blue-500' : 'bg-slate-200 dark:bg-slate-700')} />
          ))}
        </div>
      </div>
    );
  }
  const complete = progress.percent >= 100;
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-[11px] text-slate-500 dark:text-slate-400">
        <span>{progress.label}</span>
        <span className={cn('tabular-nums', complete && 'font-medium text-emerald-600')}>{progress.percent}%</span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700" aria-label={`${progress.label} ${progress.percent}%`}>
        <div className={cn('h-full rounded-full', complete ? 'bg-emerald-500' : 'bg-primary')} style={{ width: `${progress.percent}%` }} />
      </div>
    </div>
  );
}

/**
 * Board card: who it is for, how far it has got in its current stage, who owns it and
 * when it is due. Priority and health only appear when they need attention.
 */
export default function ServiceRequestCardFace({
  request,
  select,
  actions,
  highlight = false,
}: {
  request: ServiceRequestListItem;
  select: ReactNode;
  actions: ReactNode;
  /** Just created: outlined so it is easy to find. */
  highlight?: boolean;
}) {
  const collecting = request.stage === 'draft' || request.stage === 'changes_required';
  const approved = !PRE_APPROVAL_STAGES.has(request.stage);
  const started = (request.submission_percent ?? 0) > 0;
  const nextStep = request.stage === 'changes_required'
    ? 'Update and resubmit'
    : started ? 'Continue requirements' : 'Start requirements';
  const context = [request.customer, request.venue_site].filter((value) => Boolean(value && value.trim())).join(' · ');
  const urgent = request.priority === 'urgent' || request.priority === 'high';
  const flagged = request.rag === 'blocked' || (approved && request.rag === 'at_risk');
  const ownerName = request.owner?.name ?? null;

  return (
    <div className={cn(
      'relative rounded-md border bg-card text-card-foreground transition-colors hover:bg-accent/30',
      highlight && 'ring-2 ring-primary ring-offset-2 ring-offset-background',
    )}>
      <div className="space-y-3 px-3.5 py-3">
        <div className="flex items-center gap-2">
          <span className="text-[12px] font-semibold leading-4 text-primary">{request.key}</span>
          {request.request_type_name ? (
            <span className="truncate rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">{request.request_type_name}</span>
          ) : null}
          <span className="ml-auto flex items-center gap-1.5">
            {flagged ? <span className={cn('h-2 w-2 rounded-full', ragDotClass(request.rag))} title={ragLabel(request.rag)} aria-label={ragLabel(request.rag)} /> : null}
            {actions ? <span className="-mr-1 -my-1">{actions}</span> : null}
          </span>
        </div>

        <div className="relative">
          {select ? <div className="absolute left-0 top-0.5">{select}</div> : null}
          <p className={cn('text-sm font-semibold leading-5 text-slate-900 dark:text-slate-100', select && 'pl-6')}>{request.title}</p>
          {context ? <p className="mt-0.5 truncate text-[12px] leading-4 text-slate-500 dark:text-slate-400">{context}</p> : null}
        </div>

        {collecting && !started ? null : <Progress request={request} />}

        {request.is_blocked ? (
          <p className="flex items-start gap-1 text-[12px] leading-4 text-amber-700 dark:text-amber-300">
            <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
            {serviceRequestNextAction(request)}
          </p>
        ) : null}

        <div className="flex items-center gap-2">
          {ownerName ? (
            <span className="inline-flex min-w-0 items-center gap-1.5" title={`Owner: ${ownerName}`}>
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[10px] font-semibold text-primary">
                {initials(ownerName)}
              </span>
              <span className="truncate text-[12px] text-slate-600 dark:text-slate-300">{ownerName.split(/\s+/)[0]}</span>
            </span>
          ) : (
            <span className="text-[12px] text-slate-400">No owner yet</span>
          )}
          {urgent ? (
            <span className={cn('inline-flex items-center gap-1 text-[12px] font-medium', request.priority === 'urgent' ? 'text-rose-600' : 'text-amber-700')}>
              <Flag className="h-3.5 w-3.5" aria-hidden />
              {serviceRequestPriorityLabel(request.priority)}
            </span>
          ) : null}
          {approved ? <span className="ml-auto"><DueChip request={request} /></span> : null}
        </div>
      </div>
      {collecting ? (
        <div className="flex items-center justify-between rounded-b-2xl border-t border-slate-100 bg-primary/5 px-3.5 py-2 text-[12px] font-semibold text-primary dark:border-slate-800">
          {nextStep}
          <ArrowRight className="h-3.5 w-3.5" aria-hidden />
        </div>
      ) : null}
    </div>
  );
}
